import Lookup from "../../entities/lookup";

/**
 * Lookup actions
 *
 * Call order:
 *
 * LOOKUP_SAVE_QUEUE:	adds the item to the save queue
 * --> LOOKUP_SAVE:	modifies the state
 * --> LOOKUP_SAVE_SUCCESS: if all went well
 * --> LOOKUP_SAVE_FAILURE:	if either the queue or local save failed
 *
 * Note that the only way to fail LOOKUP_SAVE_QUEUE is during the call to LOOKUP_SAVE,
 * as adding an item to the queue should never fail.
 *
 * OWA-1018: the reducer shows a queued save at LOOKUP_SAVE_QUEUE, marked pending, and only that
 * save's own outcome settles the mark: LOOKUP_SAVE_SUCCESS, LOOKUP_SAVE_REVERT (refused, or
 * never queued), a refusal the queue never retries, or LOOKUP_PENDING_CHECK (it left the queue).
 */

export const LOOKUP_FETCH_ONE_QUEUE: any = 'LOOKUP_FETCH_ONE_QUEUE';
export const LOOKUP_FETCH_SUCCESS: any = 'LOOKUP_FETCH_SUCCESS';
export const LOOKUP_FETCH_FAILURE: any = 'LOOKUP_FETCH_FAILURE';
export const LOOKUP_FETCH_ONE_SUCCESS: any = 'LOOKUP_FETCH_ONE_SUCCESS';
export const LOOKUP_FETCH_ONE_FAILURE: any = 'LOOKUP_FETCH_ONE_FAILURE';

export const LOOKUP_SAVE: any = 'LOOKUP_SAVE';
export const LOOKUP_SAVE_QUEUE: any = 'LOOKUP_SAVE_QUEUE';
export const LOOKUP_SAVE_SUCCESS: any = 'LOOKUP_SAVE_SUCCESS';
export const LOOKUP_SAVE_FAILURE: any = 'LOOKUP_SAVE_FAILURE';

export const LOOKUP_SAVE_MANY: any = 'LOOKUP_SAVE_MANY';
export const LOOKUP_SAVE_MANY_QUEUE: any = 'LOOKUP_SAVE_MANY_QUEUE';
export const LOOKUP_SAVE_MANY_SUCCESS: any = 'LOOKUP_SAVE_MANY_SUCCESS';
export const LOOKUP_SAVE_MANY_FAILURE: any = 'LOOKUP_SAVE_MANY_FAILURE';

export const LOOKUP_REMOVE: any = 'LOOKUP_REMOVE';
export const LOOKUP_REMOVE_QUEUE: any = 'LOOKUP_REMOVE_QUEUE';
export const LOOKUP_REMOVE_SUCCESS: any = 'LOOKUP_REMOVE_SUCCESS';
export const LOOKUP_REMOVE_FAILURE: any = 'LOOKUP_REMOVE_FAILURE';

export const LOOKUP_RESTORE: any = 'LOOKUP_RESTORE';
export const LOOKUP_RESTORE_QUEUE: any = 'LOOKUP_RESTORE_QUEUE';
export const LOOKUP_RESTORE_SUCCESS: any = 'LOOKUP_RESTORE_SUCCESS';
export const LOOKUP_RESTORE_FAILURE: any = 'LOOKUP_RESTORE_FAILURE';

export const LOOKUP_RESTORE_MANY: any = 'LOOKUP_RESTORE_MANY';
export const LOOKUP_REMOVE_MANY: any = 'LOOKUP_REMOVE_MANY';

// OWA-1018: settle this tab's pending save of a record
export const LOOKUP_SAVE_REVERT: any = 'LOOKUP_SAVE_REVERT';
export const LOOKUP_PENDING_CHECK: any = 'LOOKUP_PENDING_CHECK';

/* Fetch actions */
export const fetchLookupSuccess = (lookups: Lookup[], partial: boolean, lastFetch: number) => ({type: LOOKUP_FETCH_SUCCESS, lookups, partial, lastFetch});
export const fetchLookupFailure = (err: any) => {
		console.error('LookupAction: fetchLookupFailure', err);
		return {type: LOOKUP_FETCH_FAILURE, err};
};
export const fetchOneLookupQueue = (lookupId: string, sentAddedOn?: number) => ({type: LOOKUP_FETCH_ONE_QUEUE, lookupId, sentAddedOn});
export const fetchOneLookupSuccess = (lookup: Lookup | null, sentAddedOn?: number, lookupId: string = lookup?.id) => ({type: LOOKUP_FETCH_ONE_SUCCESS, lookupId, lookup, sentAddedOn});
export const fetchOneLookupFailure = (id: string, err: any) => {
		console.error('LookupAction: fetchOneLookupFailure', err);
		return {type: LOOKUP_FETCH_ONE_FAILURE, id, err};
};

/* Save actions */
export const saveLookup = (lookup: Lookup) => {
    // console.debug('⮂ LOOKUP_SAVE action', lookup?.id);
    return {type: LOOKUP_SAVE, lookup};
}
export const saveLookupQueue = (lookup: Lookup, onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ LOOKUP_SAVE_QUEUE action', lookup?.id);
    return {type: LOOKUP_SAVE_QUEUE, lookup, onQueued};
}
export const saveLookupSuccess = (lookup: Lookup, sentAddedOn?: number) => {
    // console.debug('⮂ LOOKUP_SAVE_SUCCESS action', lookup?.id);
    return {type: LOOKUP_SAVE_SUCCESS, lookup, sentAddedOn};
}
export const saveLookupFailure = (lookup: Lookup, status: number, message: string) => {
    console.warn(`⮂ LOOKUP_SAVE_FAILURE error saving lookup: HTTP ${status}: ${message}`, lookup?.id);
    return {type: LOOKUP_SAVE_FAILURE, lookup, status, message};
};

/* SaveMany actions */
export const saveManyLookups = (lookups: Lookup[]) => {
    // console.debug('⮂ LOOKUP_SAVE_MANY action', lookups?.map(lookup => lookup?.id).join(','));
    return {type: LOOKUP_SAVE_MANY, lookups};
}
export const saveManyLookupsQueue = (lookups: Lookup[], onQueued?: (saved: boolean) => void) => {
    // console.debug('⮂ LOOKUP_SAVE_MANY_QUEUE action', lookups?.map(lookup => lookup.id).join(','));
    return {type: LOOKUP_SAVE_MANY_QUEUE, lookups, onQueued};
}

/** `sentAddedOns`: the queue key each answer acknowledges, aligned with `lookups` (OWA-1018) */
export const saveManyLookupsSuccess = (lookups: Lookup[], sentAddedOns?: (number | undefined)[]) => {
    // console.debug('⮂ LOOKUP_SAVE_MANY_SUCCESS action', lookups?.map(lookup => lookup.id).join(','));
    return {type: LOOKUP_SAVE_MANY_SUCCESS, lookups, sentAddedOns};
}
export const saveManyLookupsFailure = (lookups: Lookup[], status: number, message: string) => {
		console.warn(`⮂ SAVE_MANY_LOOKUPS_FAILURE Error saving lookups: HTTP ${status}: ${message}`, lookups?.map(lookup => lookup.id).join(','));
		return {type: LOOKUP_SAVE_MANY_FAILURE, lookups, status, message};
};

/* Remove actions */
export const removeLookup = (lookup: Lookup) => ({ type: LOOKUP_REMOVE, lookup });
export const removeLookupQueue = (lookup: Lookup) => ({ type: LOOKUP_REMOVE_QUEUE, lookup });
export const removeLookupSuccess = (lookup: Lookup) => ({ type: LOOKUP_REMOVE_SUCCESS, lookup });
export const removeLookupFailure = (lookup: Lookup, status: number, message: string) => {
	console.error(`Redux: removeLookupFailure Error removing lookup: HTTP ${status}: ${message}`);
	return ({ type: LOOKUP_REMOVE_FAILURE, lookup, status, message });
};

/* Restore actions */
export const restoreLookup = (lookup: Lookup) => ({ type: LOOKUP_RESTORE, lookup });
export const restoreLookupQueue = (lookup: Lookup) => ({ type: LOOKUP_RESTORE_QUEUE, lookup });
export const restoreLookupSuccess = (lookup: Lookup, sentAddedOn?: number) => ({ type: LOOKUP_RESTORE_SUCCESS, lookup, sentAddedOn });
export const restoreLookupFailure = (lookup: Lookup, status: number, message: string) => {
	console.error(`Redux: restoreLookupFailure Error restoring lookup: HTTP ${status}: ${message}`);
	return ({ type: LOOKUP_RESTORE_FAILURE, lookup, status, message });
};

/* RestoreMany actions */
export const restoreManyLookups = (lookups: Lookup[]) => ({ type: LOOKUP_RESTORE_MANY, lookups });

/* RemoveMany actions */
export const removeManyLookups = (lookups: Lookup[]) => ({ type: LOOKUP_REMOVE_MANY, lookups });

/* Pending save actions (OWA-1018) */
/**
 * The save `lookup` (queue key `sentAddedOn`, default its addedOn) will not be accepted: it was
 * refused, or never reached the queue. Its row returns to the latest accepted version, or to
 * `current` (the record as the server holds it) when that is newer; `keep` leaves the row shown.
 */
export const revertLookupSave = (lookup: Lookup, options: {sentAddedOn?: number, current?: Lookup | null, keep?: boolean} = {}) =>
    ({type: LOOKUP_SAVE_REVERT, lookup, ...options});
/** processQueue read the shared queue at `readAt` (performance.now()): the live and the permanently refused queue keys */
export const checkLookupPendingSaves = (live: number[], refused: number[], readAt: number) =>
    ({type: LOOKUP_PENDING_CHECK, live, refused, readAt});